import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Send, Home, CheckCircle, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { motion } from "framer-motion";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EUROVISION_POINTS = [12, 10, 8, 7, 6, 5, 4, 3, 2, 1];

const CountryVote = () => {
  const navigate = useNavigate();
  const [country, setCountry] = useState(null);
  const [countries, setCountries] = useState([]);
  const [votes, setVotes] = useState({});
  const [usedPoints, setUsedPoints] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasVoted, setHasVoted] = useState(false);

  const fetchCountries = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/countries`);
      setCountries(response.data);
    } catch (error) {
      toast.error("Error al cargar países");
    }
  }, []);

  const checkVoteStatus = useCallback(async (countryId) => {
    try {
      const response = await axios.get(`${API}/country/vote-status/${countryId}`);
      if (response.data.has_voted) {
        setHasVoted(true);
        toast.info("Ya has emitido tu voto");
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.error("Error checking vote status", error);
    }
  }, []);

  useEffect(() => {
    const storedCountry = localStorage.getItem('country');
    if (!storedCountry) {
      toast.error("Debes iniciar sesión primero");
      navigate('/country-login');
      return;
    }

    const parsedCountry = JSON.parse(storedCountry);
    setCountry(parsedCountry);
    fetchCountries();
    checkVoteStatus(parsedCountry.id);
  }, [navigate, fetchCountries, checkVoteStatus]);

  const handlePointSelect = (countryId, points) => {
    if (usedPoints.includes(points)) {
      const currentCountryWithThisPoint = Object.entries(votes).find(([_, p]) => p === points);
      if (currentCountryWithThisPoint) {
        const newVotes = { ...votes };
        delete newVotes[currentCountryWithThisPoint[0]];
        setVotes(newVotes);
        setUsedPoints(usedPoints.filter(p => p !== points));
      }
    }

    if (votes[countryId]) {
      const oldPoints = votes[countryId];
      const newVotes = { ...votes };
      delete newVotes[countryId];
      setVotes(newVotes);
      setUsedPoints(usedPoints.filter(p => p !== oldPoints));
      return;
    }

    setVotes({ ...votes, [countryId]: points });
    setUsedPoints([...usedPoints, points]);
  };

  const handleSubmit = async () => {
    if (Object.keys(votes).length !== 10) {
      toast.error("Debes asignar los 10 puntos");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/country/vote`, {
        country_id: country.id,
        votes
      });
      toast.success("¡Voto enviado exitosamente!");
      setHasVoted(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al enviar voto");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('country');
    navigate('/country-login');
  };

  if (!country) return null;

  const eligibleCountries = countries.filter(c => c.id !== country.id);
  const remainingPoints = EUROVISION_POINTS.filter(p => !usedPoints.includes(p));

  return (
    <div className="min-h-screen bg-zinc-950 p-4 pb-32">
      <div className="max-w-2xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 data-testid="vote-title" className="font-unbounded font-bold text-2xl md:text-4xl text-white">Votación</h1>
            <p className="text-zinc-400 mt-1">País: <span className="text-cyan-400 font-semibold">{country.name}</span></p>
          </div>
          <Button 
            data-testid="btn-logout"
            onClick={handleLogout} 
            variant="ghost" 
            className="text-zinc-400 hover:text-white"
          >
            <Home className="w-4 h-4 mr-2" />
            Salir
          </Button>
        </div>

        {hasVoted ? (
          <Card className="bg-green-900/20 border-green-800 p-8 text-center">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h2 className="font-unbounded text-2xl text-white mb-2">¡Voto Enviado!</h2>
            <p className="text-zinc-400">Tu votación ha sido registrada correctamente</p>
          </Card>
        ) : (
          <>
            <div className="sticky top-0 z-20 bg-zinc-900/95 backdrop-blur-xl border border-zinc-800 rounded-2xl p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-zinc-400 text-sm">Puntos asignados</p>
                  <p className="font-mono font-bold text-2xl text-white">{Object.keys(votes).length} / 10</p>
                </div>
                <div className="text-right">
                  <p className="text-zinc-400 text-sm mb-2">Disponibles</p>
                  <div className="flex flex-wrap gap-2 justify-end">
                    {remainingPoints.map(point => (
                      <span key={point} className="font-mono font-bold text-sm bg-zinc-800 px-3 py-1 rounded-full text-pink-400">
                        {point}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-3">
              {eligibleCountries.map((c, index) => (
                <motion.div
                  key={c.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <Card 
                    data-testid={`country-card-${c.id}`}
                    className={`p-4 transition-all ${
                      votes[c.id] 
                        ? 'bg-pink-900/30 border-pink-500 shadow-[0_0_20px_rgba(236,72,153,0.3)]' 
                        : 'bg-zinc-900/80 border-zinc-800 hover:border-zinc-700'
                    }`}
                  >
                    <div className="flex items-center gap-4">
                      <img 
                        src={c.flag_url} 
                        alt={c.name}
                        className="w-20 h-14 object-contain"
                        onError={(e) => e.target.src = 'https://placehold.co/100x70/000/FFF?text=Flag'}
                      />
                      <div className="flex-1">
                      <h3 className="font-unbounded font-semibold text-white text-lg">
                        {c.running_order ? <span className="text-cyan-400 font-mono mr-1">{String(c.running_order).padStart(2, '0')} ·</span> : null}
                        {c.name}
                      </h3>
                        {votes[c.id] && (
                          <p className="text-pink-400 text-sm font-mono font-bold mt-1">
                            {votes[c.id]} puntos asignados
                          </p>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-2 max-w-[200px]">
                        {EUROVISION_POINTS.map(point => (
                          <button
                            key={point}
                            data-testid={`btn-point-${c.id}-${point}`}
                            onClick={() => handlePointSelect(c.id, point)}
                            disabled={usedPoints.includes(point) && votes[c.id] !== point}
                            className={`w-10 h-10 rounded-full font-mono font-bold text-sm transition-all ${
                              votes[c.id] === point
                                ? 'bg-pink-600 text-white scale-110 shadow-[0_0_15px_rgba(236,72,153,0.5)]'
                                : usedPoints.includes(point)
                                ? 'bg-zinc-800 text-zinc-600 cursor-not-allowed'
                                : 'bg-zinc-800 text-white hover:bg-pink-600/50 hover:scale-105'
                            }`}
                          >
                            {point}
                          </button>
                        ))}
                      </div>
                    </div>
                  </Card>
                </motion.div>
              ))}
            </div>

            <div className="fixed bottom-0 left-0 right-0 p-4 bg-zinc-950/95 backdrop-blur-xl border-t border-zinc-800">
              <div className="max-w-2xl mx-auto">
                <Button
                  data-testid="btn-submit-vote"
                  onClick={handleSubmit}
                  disabled={Object.keys(votes).length !== 10 || loading}
                  className="w-full h-16 bg-gradient-to-r from-pink-600 to-cyan-600 hover:from-pink-500 hover:to-cyan-500 disabled:from-zinc-800 disabled:to-zinc-800 text-white font-unbounded font-bold text-lg shadow-[0_0_30px_rgba(236,72,153,0.4)] transition-all"
                >
                  {loading ? (
                    "Enviando..."
                  ) : Object.keys(votes).length !== 10 ? (
                    `Selecciona ${10 - Object.keys(votes).length} país(es) más`
                  ) : (
                    <>
                      <Send className="w-5 h-5 mr-2" />
                      Enviar Votación
                    </>
                  )}
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CountryVote;